/**
 * InlineAdder — "+" gutter that lives between blocks in the rendered
 * read-view. Hover → "+" appears; click → 3-button menu; click type
 * → inline input field; Enter → POST /sections/ → new section
 * appears at that exact position (collab Yjs WS pushes it back).
 *
 * Replaces the editor-only InlineBlockMenu for read-view editing.
 * Alex 2026-05-30: "ich stelle mir auch einen Edit-Modus vor für
 * mich im Read, dann möchte ich auch dieses Plus haben und dann
 * einfach schön eingeblendet ein Feld bekommen".
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  HelpCircle,
  CheckCircle,
  Box,
  Vote as VoteIcon,
} from 'lucide-react'
import { CONTENT_API } from '../config'
import { getAccessToken, getUser } from '../auth'

type SectionType = 'question' | 'task' | 'product' | 'vote'
type Mode = 'idle' | 'menu' | 'form'

/** A summary of an existing section in the post, used by the
 *  Vote-Erweitert form's "Ziel-Section" picker for derived
 *  actions (task_assign / status_change). */
export interface SectionRef {
  id: string
  type: 'product' | 'question' | 'task' | 'annotation' | 'vote'
  label: string
}

interface Props {
  postId: number
  /** Insert AFTER this 0-based paragraph index. -1 = prepend. */
  paragraphIndex: number
  /** Notified after a successful create. */
  onCreated?: (sectionId: string) => void
  /** Agents in the post's notify_authors — used by the vote form to
   * pre-fill options with all agents (Alex' "von den Agenten im Doc"
   * shortcut). */
  notifyAuthors?: string[]
  /** Sections already in the doc — populates the Vote-Erweitert
   *  action_target picker. */
  existingSections?: SectionRef[]
}

export function InlineAdder({
  postId,
  paragraphIndex,
  onCreated,
  notifyAuthors,
  existingSections,
}: Props) {
  const [mode, setMode] = useState<Mode>('idle')
  const [type, setType] = useState<SectionType | null>(null)
  const [text, setText] = useState('')
  const [voteOptions, setVoteOptions] = useState('')
  const [voteThreshold, setVoteThreshold] = useState('majority')
  // Vote-Erweitert state (Option B — Alex 2026-06-02).
  // voteVotersMode = 'open' → server defaults to all notify_authors.
  // voteVotersMode = 'select' → use voteVoters explicit list.
  const [voteVotersMode, setVoteVotersMode] = useState<'open' | 'select'>('open')
  const [voteVoters, setVoteVoters] = useState<string[]>([])
  const [voteActionType, setVoteActionType] = useState<'none' | 'task_assign' | 'status_change'>('none')
  const [voteActionTarget, setVoteActionTarget] = useState<string>('')
  // Task-Erweitert state (Alex Option B 2026-06-02, Pt. 2):
  // taskAssignee 'select' value, taskPriority dropdown,
  // taskTargets multi-pick for additional CC recipients.
  const [taskAssignee, setTaskAssignee] = useState<string>('')
  const [taskPriority, setTaskPriority] = useState<string>('normal')
  const [taskTargets, setTaskTargets] = useState<string[]>([])
  // Question-Erweitert state:
  //   'broadcast' = no targets, no primary → alle sehen, kein Lead
  //   'targets'   = explicit recipient list → nur die sehen
  //   'primary'   = singular primary → alle sehen, einer ist Lead
  //
  // Alex Round 3 (2026-06-02): die drei Modi machen das routing
  // explizit. Bisheriges Verhalten der Erweitert war 'targets'.
  // 'primary' war im Backend schon da (primary attr), aber im UI
  // nicht erreichbar.
  const [questionRoutingMode, setQuestionRoutingMode] = useState<
    'broadcast' | 'targets' | 'primary'
  >('broadcast')
  const [questionTargets, setQuestionTargets] = useState<string[]>([])
  const [questionPrimary, setQuestionPrimary] = useState<string>('')
  // Product-Erweitert state (Alex 2026-06-19): Mode-Picker, Goal,
  // Owner/Editors/Reviewers-Rollen, Body als Initial-Artefakt.
  //   mode: 'draft' (Erstentwurf von Null), 'improve' (existing
  //   verbessern), 'review' (existing prüfen), 'final' (lock).
  //   owner: stable identity der Hauptschreiberin (heute aus
  //   notify_authors-Dropdown — auch Eigen-Select via Mensch-Name
  //   möglich).
  //   editors: dürfen Body editieren.
  //   reviewers: müssen approven (sections_approve).
  const [productMode, setProductMode] = useState<
    'draft' | 'improve' | 'review' | 'final'
  >('draft')
  const [productGoal, setProductGoal] = useState('')
  const [productOwner, setProductOwner] = useState('')
  const [productEditors, setProductEditors] = useState<string[]>([])
  const [productReviewers, setProductReviewers] = useState<string[]>([])
  const [productBody, setProductBody] = useState('')
  const [saving, setSaving] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  // Click-outside / Escape collapse back to idle.
  useEffect(() => {
    if (mode === 'idle') return
    const onDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        reset()
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') reset()
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  // Autofocus the input when the form opens.
  useEffect(() => {
    if (mode === 'form') {
      window.setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [mode])

  const reset = () => {
    setMode('idle')
    setType(null)
    setText('')
    setVoteOptions('')
    setVoteThreshold('majority')
    setVoteVotersMode('open')
    setVoteVoters([])
    setVoteActionType('none')
    setVoteActionTarget('')
    setTaskAssignee('')
    setTaskPriority('normal')
    setTaskTargets([])
    setQuestionRoutingMode('broadcast')
    setQuestionTargets([])
    setQuestionPrimary('')
    setProductMode('draft')
    setProductGoal('')
    setProductOwner('')
    setProductEditors([])
    setProductReviewers([])
    setProductBody('')
  }

  const pickType = (t: SectionType) => {
    setType(t)
    setText('')
    setMode('form')
  }

  const submit = useCallback(async () => {
    if (!type) return
    const trimmed = text.trim()
    if (!trimmed) return
    const token = getAccessToken()
    if (!token) return
    const user = getUser()
    const initiator = user
      ? `${user.first_name} ${user.last_name}`.trim() || user.email
      : 'Unknown'
    const attrs: Record<string, string> = { initiator }
    let initialText = ''
    if (type === 'question') {
      attrs.status = 'open'
      initialText = trimmed
      // Question-Erweitert routing-mode dispatch:
      //   'targets' → server narrows fan-out to that subset
      //   'primary' → broadcast + named Lead (alle sehen, einer reagiert)
      //   'broadcast' → keine routing attrs, default
      if (questionRoutingMode === 'targets') {
        if (questionTargets.length === 0) {
          alert(
            'Im Modus "Nur ausgewählte Empfänger" mindestens einen ' +
              'Empfänger wählen, oder auf einen anderen Modus wechseln.',
          )
          return
        }
        attrs.targets = questionTargets.join(',')
      } else if (questionRoutingMode === 'primary') {
        if (!questionPrimary.trim()) {
          alert(
            'Im Modus "Alle sehen, X soll antworten" einen Lead-Agent ' +
              'wählen, oder auf einen anderen Modus wechseln.',
          )
          return
        }
        attrs.primary = questionPrimary.trim()
      }
    } else if (type === 'task') {
      attrs.status = 'open'
      // Task-Erweitert: text becomes the body description
      // (initial_text), assignee/priority/targets come from the
      // advanced picker. Backward-compat for the quick path: if
      // the user didn't open Erweitert and typed only a name into
      // the textbox, we still treat the text as body content. The
      // pre-Option-B "text = assignee" semantic is gone — clearer
      // to keep body separate.
      initialText = trimmed
      if (taskAssignee.trim()) {
        attrs.assignee = taskAssignee.trim()
      }
      if (taskPriority && taskPriority !== 'normal') {
        attrs.priority = taskPriority
      }
      if (taskTargets.length > 0) {
        attrs.targets = taskTargets.join(',')
      }
    } else if (type === 'product') {
      attrs.name = trimmed
      attrs.mode = productMode
      if (productGoal.trim()) attrs.goal = productGoal.trim()
      if (productOwner.trim()) attrs.owner = productOwner.trim()
      if (productEditors.length > 0) {
        attrs.editors = productEditors.join(',')
      }
      if (productReviewers.length > 0) {
        attrs.reviewers = productReviewers.join(',')
      }
      // Product-Body landet als initial_text — das IST das Artefakt.
      // Name + Goal sind Metadata, Body ist der eigentliche Inhalt
      // den Editors später per CRDT mutieren. Leerer Body (Advanced
      // nicht geöffnet / draft-Modus ohne Stub) → minimaler Titel-
      // Stub, weil der CRDT-Pfad leeres initial_text ablehnt.
      initialText = productBody.trim() || `# ${trimmed}`
    } else if (type === 'vote') {
      // Vote requires explicit options. A vote with options='open'
      // is just a Frage — the dedicated Question section already
      // handles that pattern.
      const optsList = voteOptions
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      if (optsList.length < 2) {
        alert(
          'Mindestens zwei Optionen kommasepariert eintragen, ' +
            'sonst ist es keine Abstimmung (für freie Antworten ' +
            'gibt es die Frage-Section).',
        )
        return
      }
      attrs.question = trimmed
      attrs.options = optsList.join(',')
      attrs.threshold = voteThreshold
      // voters: 'open' = all notify_authors (default); 'select' =
      // explicit comma-list of agent names from the picker.
      if (voteVotersMode === 'select') {
        if (voteVoters.length < 1) {
          alert('Mindestens einen Voter auswählen oder auf "Alle Doc-Agenten" umschalten.')
          return
        }
        attrs.voters = voteVoters.join(',')
      } else {
        attrs.voters = 'open'
      }
      // Vote-Erweitert governance action (Iteration D A.D.2):
      // when set, the vote winner mutates another section on tally.
      if (voteActionType !== 'none') {
        if (!voteActionTarget.trim()) {
          alert('Bei einer Governance-Aktion bitte eine Ziel-Section auswählen.')
          return
        }
        attrs.action_type = voteActionType
        attrs.action_target = voteActionTarget.trim()
      }
      attrs.status = 'open'
      initialText = ''
    }
    setSaving(true)
    try {
      const r = await fetch(
        `${CONTENT_API}/api/v1/posts/${postId}/sections/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type,
            attrs,
            initial_text: initialText,
            paragraph_index: paragraphIndex,
          }),
        },
      )
      if (!r.ok) {
        const t = await r.text()
        alert(`Section konnte nicht angelegt werden: ${r.status} ${t}`)
        return
      }
      const data = (await r.json()) as { id?: string }
      if (data.id) onCreated?.(data.id)
      reset()
    } catch (e) {
      alert(`Netzwerk-Fehler: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSaving(false)
    }
  }, [type, text, postId, paragraphIndex, onCreated])

  return (
    <div className="inline-adder" ref={wrapperRef}>
      {mode === 'idle' && (
        <button
          type="button"
          className="inline-adder-plus"
          onClick={() => setMode('menu')}
          aria-label="Hier etwas einfügen"
          title="Hier eine Frage, Aufgabe, Product oder Abstimmung einfügen"
        >
          +
        </button>
      )}
      {mode === 'menu' && (
        <div className="inline-adder-menu">
          <button
            type="button"
            className="inline-adder-menu-item"
            onClick={() => pickType('question')}
          >
            <HelpCircle size={14} strokeWidth={1.75} />
            <span className="inline-adder-menu-label">Frage</span>
          </button>
          <button
            type="button"
            className="inline-adder-menu-item"
            onClick={() => pickType('task')}
          >
            <CheckCircle size={14} strokeWidth={1.75} />
            <span className="inline-adder-menu-label">Aufgabe</span>
          </button>
          <button
            type="button"
            className="inline-adder-menu-item"
            onClick={() => pickType('product')}
          >
            <Box size={14} strokeWidth={1.75} />
            <span className="inline-adder-menu-label">Product</span>
          </button>
          <button
            type="button"
            className="inline-adder-menu-item"
            onClick={() => pickType('vote')}
          >
            <VoteIcon size={14} strokeWidth={1.75} />
            <span className="inline-adder-menu-label">Abstimmung</span>
          </button>
          <button
            type="button"
            className="inline-adder-cancel"
            onClick={reset}
            aria-label="Abbrechen"
          >
            ×
          </button>
        </div>
      )}
      {mode === 'form' && type && type !== 'vote' && (
        <InlineForm
          type={type}
          text={text}
          setText={setText}
          saving={saving}
          onSubmit={submit}
          onCancel={reset}
          inputRef={inputRef}
          notifyAuthors={notifyAuthors}
          taskAssignee={taskAssignee}
          setTaskAssignee={setTaskAssignee}
          taskPriority={taskPriority}
          setTaskPriority={setTaskPriority}
          taskTargets={taskTargets}
          setTaskTargets={setTaskTargets}
          questionRoutingMode={questionRoutingMode}
          setQuestionRoutingMode={setQuestionRoutingMode}
          questionTargets={questionTargets}
          setQuestionTargets={setQuestionTargets}
          questionPrimary={questionPrimary}
          setQuestionPrimary={setQuestionPrimary}
          productMode={productMode}
          setProductMode={setProductMode}
          productGoal={productGoal}
          setProductGoal={setProductGoal}
          productOwner={productOwner}
          setProductOwner={setProductOwner}
          productEditors={productEditors}
          setProductEditors={setProductEditors}
          productReviewers={productReviewers}
          setProductReviewers={setProductReviewers}
          productBody={productBody}
          setProductBody={setProductBody}
        />
      )}
      {mode === 'form' && type === 'vote' && (
        <VoteForm
          question={text}
          setQuestion={setText}
          options={voteOptions}
          setOptions={setVoteOptions}
          threshold={voteThreshold}
          setThreshold={setVoteThreshold}
          votersMode={voteVotersMode}
          setVotersMode={setVoteVotersMode}
          voters={voteVoters}
          setVoters={setVoteVoters}
          actionType={voteActionType}
          setActionType={setVoteActionType}
          actionTarget={voteActionTarget}
          setActionTarget={setVoteActionTarget}
          saving={saving}
          onSubmit={submit}
          onCancel={reset}
          inputRef={inputRef as React.RefObject<HTMLTextAreaElement | null>}
          notifyAuthors={notifyAuthors}
          existingSections={existingSections}
        />
      )}
    </div>
  )
}


function VoteForm({
  question,
  setQuestion,
  options,
  setOptions,
  threshold,
  setThreshold,
  votersMode,
  setVotersMode,
  voters,
  setVoters,
  actionType,
  setActionType,
  actionTarget,
  setActionTarget,
  saving,
  onSubmit,
  onCancel,
  inputRef,
  notifyAuthors,
  existingSections,
}: {
  question: string
  setQuestion: (v: string) => void
  options: string
  setOptions: (v: string) => void
  threshold: string
  setThreshold: (v: string) => void
  votersMode: 'open' | 'select'
  setVotersMode: (v: 'open' | 'select') => void
  voters: string[]
  setVoters: (v: string[]) => void
  actionType: 'none' | 'task_assign' | 'status_change'
  setActionType: (v: 'none' | 'task_assign' | 'status_change') => void
  actionTarget: string
  setActionTarget: (v: string) => void
  saving: boolean
  onSubmit: () => void
  onCancel: () => void
  inputRef: React.RefObject<HTMLTextAreaElement | null>
  notifyAuthors?: string[]
  existingSections?: SectionRef[]
}) {
  const [advanced, setAdvanced] = useState(false)
  // Alex 2026-06-02: "ich möchte oft Votings machen wo es darum geht
  // das meine eine der notify agenten also alle agenten im dok zur
  // auswahl hat … kannst du die option einbauen dann legt er das so
  // an das er die namen der agenten nimmt". The button is only
  // active when the doc actually has notify_authors and the field is
  // either empty or different from the agent list.
  const agentNames = (notifyAuthors ?? []).filter(
    (n) => typeof n === 'string' && n.trim(),
  )
  const agentString = agentNames.join(',')
  const canUseAgents =
    agentNames.length >= 2 && options.trim() !== agentString
  return (
    <div className="inline-adder-form inline-adder-form--vote">
      <span className="inline-adder-form-icon">
        <VoteIcon size={14} strokeWidth={1.75} />
      </span>
      <div className="inline-adder-vote-fields">
        <label className="inline-adder-vote-label">FRAGE</label>
        <textarea
          ref={inputRef}
          className="inline-adder-form-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Worüber wird abgestimmt?"
          rows={2}
          disabled={saving}
        />
        <div className="inline-adder-vote-label-row">
          <label className="inline-adder-vote-label">
            OPTIONEN (Pflicht, mindestens 2, mit Komma getrennt)
          </label>
          {canUseAgents && (
            <button
              type="button"
              className="inline-adder-vote-agents-btn"
              onClick={() => setOptions(agentString)}
              disabled={saving}
              title={
                'Optionen aus den Doc-Agenten übernehmen: ' + agentString
              }
            >
              Doc-Agenten übernehmen ({agentNames.length})
            </button>
          )}
        </div>
        <input
          type="text"
          className="inline-adder-form-input"
          value={options}
          onChange={(e) => setOptions(e.target.value)}
          placeholder="z.B. Codex,Content,Gemini"
          disabled={saving}
        />
        <label className="inline-adder-vote-label">SCHWELLE</label>
        <select
          className="inline-adder-form-input"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          disabled={saving}
        >
          <option value="majority">Mehrheit (&gt;50% aller Stimmen)</option>
          <option value="all">Einstimmig (alle stimmen, gleiche Wahl)</option>
        </select>

        {/* Alex 2026-06-02 Option B: Erweitert-Toggle reveals voters
            picker + derived-action governance fields. Quick-Vote bleibt
            default (voters=open, action_type=none). */}
        <button
          type="button"
          className="inline-adder-vote-advanced-toggle"
          onClick={() => setAdvanced((v) => !v)}
          disabled={saving}
        >
          {advanced ? '▾ Erweitert' : '▸ Erweitert (Wähler + Governance-Aktion)'}
        </button>

        {advanced && (
          <>
            <label className="inline-adder-vote-label">WÄHLER</label>
            <div className="inline-adder-vote-voters-mode">
              <label>
                <input
                  type="radio"
                  name="voters-mode"
                  checked={votersMode === 'open'}
                  onChange={() => setVotersMode('open')}
                  disabled={saving}
                />
                Alle Doc-Agenten
                {(notifyAuthors?.length ?? 0) > 0 && (
                  <span className="inline-adder-vote-hint">
                    ({notifyAuthors!.length})
                  </span>
                )}
              </label>
              <label>
                <input
                  type="radio"
                  name="voters-mode"
                  checked={votersMode === 'select'}
                  onChange={() => setVotersMode('select')}
                  disabled={saving || (notifyAuthors?.length ?? 0) === 0}
                />
                Auswahl
              </label>
            </div>
            {votersMode === 'select' && (
              <div className="inline-adder-vote-voters-list">
                {(notifyAuthors ?? []).map((name) => {
                  const checked = voters.includes(name)
                  return (
                    <label key={name}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setVoters([...voters, name])
                          } else {
                            setVoters(voters.filter((v) => v !== name))
                          }
                        }}
                        disabled={saving}
                      />
                      {name}
                    </label>
                  )
                })}
              </div>
            )}

            <label className="inline-adder-vote-label">
              GOVERNANCE-AKTION (Vote-Winner mutiert eine Section)
            </label>
            <select
              className="inline-adder-form-input"
              value={actionType}
              onChange={(e) => {
                const v = e.target.value as
                  | 'none'
                  | 'task_assign'
                  | 'status_change'
                setActionType(v)
                if (v === 'none') setActionTarget('')
              }}
              disabled={saving}
            >
              <option value="none">Keine — nur abstimmen</option>
              <option value="task_assign">
                task_assign — Winner wird assignee einer Task
              </option>
              <option value="status_change">
                status_change — Winner setzt Status einer Question/Task
              </option>
            </select>

            {actionType !== 'none' && (
              <>
                <label className="inline-adder-vote-label">ZIEL-SECTION</label>
                {(() => {
                  const candidates = (existingSections ?? []).filter((s) => {
                    if (actionType === 'task_assign') return s.type === 'task'
                    if (actionType === 'status_change')
                      return s.type === 'question' || s.type === 'task'
                    return false
                  })
                  if (candidates.length === 0) {
                    return (
                      <div className="inline-adder-vote-hint inline-adder-vote-empty-targets">
                        {actionType === 'task_assign'
                          ? 'Keine Task-Section im Post — leg erst eine Task an.'
                          : 'Keine Question- oder Task-Section im Post — leg erst eine an.'}
                      </div>
                    )
                  }
                  return (
                    <select
                      className="inline-adder-form-input"
                      value={actionTarget}
                      onChange={(e) => setActionTarget(e.target.value)}
                      disabled={saving}
                    >
                      <option value="">— wähle Ziel-Section —</option>
                      {candidates.map((s) => (
                        <option key={s.id} value={s.id}>
                          [{s.type}] {s.label.slice(0, 60)} ({s.id.slice(0, 10)})
                        </option>
                      ))}
                    </select>
                  )
                })()}
              </>
            )}
          </>
        )}
      </div>
      <button
        type="button"
        className="inline-adder-form-submit"
        onClick={onSubmit}
        disabled={
          saving ||
          !question.trim() ||
          options.split(',').map((s) => s.trim()).filter(Boolean).length < 2
        }
      >
        {saving ? '…' : '↵'}
      </button>
      <button
        type="button"
        className="inline-adder-form-cancel"
        onClick={onCancel}
        aria-label="Abbrechen"
      >
        ×
      </button>
    </div>
  )
}

function InlineForm({
  type,
  text,
  setText,
  saving,
  onSubmit,
  onCancel,
  inputRef,
  notifyAuthors,
  taskAssignee,
  setTaskAssignee,
  taskPriority,
  setTaskPriority,
  taskTargets,
  setTaskTargets,
  questionRoutingMode,
  setQuestionRoutingMode,
  questionTargets,
  setQuestionTargets,
  questionPrimary,
  setQuestionPrimary,
  productMode,
  setProductMode,
  productGoal,
  setProductGoal,
  productOwner,
  setProductOwner,
  productEditors,
  setProductEditors,
  productReviewers,
  setProductReviewers,
  productBody,
  setProductBody,
}: {
  type: Exclude<SectionType, 'vote'>
  text: string
  setText: (v: string) => void
  saving: boolean
  onSubmit: () => void
  onCancel: () => void
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>
  notifyAuthors?: string[]
  taskAssignee: string
  setTaskAssignee: (v: string) => void
  taskPriority: string
  setTaskPriority: (v: string) => void
  taskTargets: string[]
  setTaskTargets: (v: string[]) => void
  questionRoutingMode: 'broadcast' | 'targets' | 'primary'
  setQuestionRoutingMode: (v: 'broadcast' | 'targets' | 'primary') => void
  questionTargets: string[]
  setQuestionTargets: (v: string[]) => void
  questionPrimary: string
  setQuestionPrimary: (v: string) => void
  productMode: 'draft' | 'improve' | 'review' | 'final'
  setProductMode: (v: 'draft' | 'improve' | 'review' | 'final') => void
  productGoal: string
  setProductGoal: (v: string) => void
  productOwner: string
  setProductOwner: (v: string) => void
  productEditors: string[]
  setProductEditors: (v: string[]) => void
  productReviewers: string[]
  setProductReviewers: (v: string[]) => void
  productBody: string
  setProductBody: (v: string) => void
}) {
  const [advanced, setAdvanced] = useState(false)
  const agents = (notifyAuthors ?? []).filter(
    (n) => typeof n === 'string' && n.trim(),
  )
  const placeholder =
    type === 'question'
      ? 'Deine Frage … (Enter zum Anlegen)'
      : type === 'task'
        ? 'Was soll passieren? (Aufgabe-Beschreibung)'
        : 'Name des Artefakts'
  const FormIcon =
    type === 'question'
      ? HelpCircle
      : type === 'task'
        ? CheckCircle
        : Box
  const formIconColor =
    type === 'question'
      ? '#f59e0b'
      : type === 'task'
        ? '#10b981'
        : '#3b82f6'
  const useTextarea = type === 'question' || type === 'task'
  // Alex 2026-06-19: Product bekommt jetzt auch Erweitert-Layer
  // (Mode-Picker, Goal, Owner/Editors/Reviewers, Body als Initial-
  // Artefakt). Question + Task + Product haben jetzt alle den Toggle.
  const hasAdvanced =
    type === 'question' || type === 'task' || type === 'product'
  return (
    <div className="inline-adder-form inline-adder-form--vertical">
      <div className="inline-adder-form-row">
        <span className="inline-adder-form-icon" style={{ color: formIconColor }}>
          <FormIcon size={16} strokeWidth={2} />
        </span>
        {useTextarea ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement | null>}
            className="inline-adder-form-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            rows={2}
            disabled={saving}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onSubmit()
              }
            }}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement | null>}
            type="text"
            className="inline-adder-form-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            disabled={saving}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onSubmit()
              }
            }}
          />
        )}
        <button
          type="button"
          className="inline-adder-form-submit"
          onClick={onSubmit}
          disabled={saving || !text.trim()}
        >
          {saving ? '…' : '↵'}
        </button>
        <button
          type="button"
          className="inline-adder-form-cancel"
          onClick={onCancel}
          aria-label="Abbrechen"
        >
          ×
        </button>
      </div>

      {hasAdvanced && (
        <button
          type="button"
          className="inline-adder-vote-advanced-toggle"
          onClick={() => setAdvanced((v) => !v)}
          disabled={saving}
        >
          {advanced
            ? '▾ Erweitert'
            : type === 'task'
              ? '▸ Erweitert (Zuweisung + Priorität + Empfänger)'
              : type === 'product'
                ? '▸ Erweitert (Mode + Ziel + Body + Rollen)'
                : '▸ Erweitert (Ziel-Empfänger)'}
        </button>
      )}

      {advanced && type === 'task' && (
        <div className="inline-adder-advanced">
          <label className="inline-adder-vote-label">
            ZUWEISUNG (assignee — wer macht die Aufgabe)
          </label>
          {agents.length > 0 ? (
            <select
              className="inline-adder-form-input"
              value={taskAssignee}
              onChange={(e) => setTaskAssignee(e.target.value)}
              disabled={saving}
            >
              <option value="">— offen (kein assignee) —</option>
              {agents.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          ) : (
            <div className="inline-adder-vote-hint inline-adder-vote-empty-targets">
              Keine Doc-Agenten — füg erst notify_authors zum Post hinzu.
            </div>
          )}

          <label className="inline-adder-vote-label">PRIORITÄT</label>
          <select
            className="inline-adder-form-input"
            value={taskPriority}
            onChange={(e) => setTaskPriority(e.target.value)}
            disabled={saving}
          >
            <option value="low">low — niedrig, kann warten</option>
            <option value="normal">normal — Standard</option>
            <option value="high">high — dringend</option>
          </select>

          <label className="inline-adder-vote-label">
            ZUSÄTZLICHE EMPFÄNGER (CC, optional)
          </label>
          <AgentMultiPicker
            agents={agents}
            selected={taskTargets}
            setSelected={setTaskTargets}
            saving={saving}
          />
        </div>
      )}

      {advanced && type === 'question' && (
        <div className="inline-adder-advanced">
          <label className="inline-adder-vote-label">ZIEL-ROUTING</label>
          <div className="inline-adder-question-routing">
            <label>
              <input
                type="radio"
                name="question-routing"
                checked={questionRoutingMode === 'broadcast'}
                onChange={() => setQuestionRoutingMode('broadcast')}
                disabled={saving}
              />
              Broadcast — alle sehen, kein Lead
            </label>
            <label>
              <input
                type="radio"
                name="question-routing"
                checked={questionRoutingMode === 'primary'}
                onChange={() => setQuestionRoutingMode('primary')}
                disabled={saving || agents.length === 0}
              />
              Alle sehen die Frage, einer soll antworten
              <span className="inline-adder-vote-hint">
                {' '}(primary — beste UX für „Frage an X, alle sehen Antwort")
              </span>
            </label>
            <label>
              <input
                type="radio"
                name="question-routing"
                checked={questionRoutingMode === 'targets'}
                onChange={() => setQuestionRoutingMode('targets')}
                disabled={saving || agents.length === 0}
              />
              Nur ausgewählte Empfänger sehen sie
              <span className="inline-adder-vote-hint">
                {' '}(targets — privates 1-zu-N)
              </span>
            </label>
          </div>

          {questionRoutingMode === 'primary' && agents.length > 0 && (
            <>
              <label className="inline-adder-vote-label">LEAD-AGENT</label>
              <select
                className="inline-adder-form-input"
                value={questionPrimary}
                onChange={(e) => setQuestionPrimary(e.target.value)}
                disabled={saving}
              >
                <option value="">— wähle Lead —</option>
                {agents.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              {questionPrimary && (
                <div className="inline-adder-vote-hint">
                  Lead: <strong>{questionPrimary}</strong> · alle anderen
                  Doc-Agenten sehen die Frage als Beobachter
                </div>
              )}
            </>
          )}

          {questionRoutingMode === 'targets' && agents.length > 0 && (
            <>
              <label className="inline-adder-vote-label">
                EMPFÄNGER (erster = primary, Rest = CC)
              </label>
              <AgentMultiPicker
                agents={agents}
                selected={questionTargets}
                setSelected={setQuestionTargets}
                saving={saving}
              />
              {questionTargets.length > 0 && (
                <div className="inline-adder-vote-hint">
                  Primary: <strong>{questionTargets[0]}</strong>
                  {questionTargets.length > 1 && (
                    <> · CC: {questionTargets.slice(1).join(', ')}</>
                  )}
                  {' '}· nicht-gelistete Agenten sehen die Frage NICHT
                </div>
              )}
            </>
          )}

          {agents.length === 0 && (
            <div className="inline-adder-vote-hint inline-adder-vote-empty-targets">
              Keine Doc-Agenten — primary/targets brauchen notify_authors.
            </div>
          )}
        </div>
      )}

      {advanced && type === 'product' && (
        <div className="inline-adder-advanced">
          <label className="inline-adder-vote-label">MODUS</label>
          <select
            className="inline-adder-form-input"
            value={productMode}
            onChange={(e) =>
              setProductMode(
                e.target.value as 'draft' | 'improve' | 'review' | 'final',
              )
            }
            disabled={saving}
          >
            <option value="draft">
              draft — Erstentwurf von Null
            </option>
            <option value="improve">
              improve — bestehendes verbessern
            </option>
            <option value="review">
              review — bestehendes prüfen
            </option>
            <option value="final">
              final — gelocktes Artefakt (Review-abgeschlossen)
            </option>
          </select>

          <label className="inline-adder-vote-label">
            ZIEL / GOAL (optional, was soll erreicht werden)
          </label>
          <input
            type="text"
            className="inline-adder-form-input"
            value={productGoal}
            onChange={(e) => setProductGoal(e.target.value)}
            placeholder="z.B. „geile Landing-Page mit Hero-CTA"
            disabled={saving}
          />

          <label className="inline-adder-vote-label">
            INHALT / BODY (das eigentliche Artefakt — Markdown/Code)
          </label>
          <textarea
            className="inline-adder-form-input"
            value={productBody}
            onChange={(e) => setProductBody(e.target.value)}
            placeholder={
              productMode === 'draft'
                ? 'Anfang lassen oder kurzen Stub eintippen — Editors füllen weiter'
                : 'Initialer Inhalt — Editors mutieren via CRDT-Edit'
            }
            rows={6}
            disabled={saving}
            style={{ fontFamily: 'monospace', fontSize: 13 }}
          />

          <label className="inline-adder-vote-label">
            OWNER (wer ist hauptverantwortlich)
          </label>
          {agents.length > 0 ? (
            <select
              className="inline-adder-form-input"
              value={productOwner}
              onChange={(e) => setProductOwner(e.target.value)}
              disabled={saving}
            >
              <option value="">— ich selbst (default) —</option>
              {agents.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          ) : (
            <div className="inline-adder-vote-hint inline-adder-vote-empty-targets">
              Keine Doc-Agenten — Owner bleibt du selbst.
            </div>
          )}

          <label className="inline-adder-vote-label">
            EDITORS (dürfen Body mit-editieren, optional)
          </label>
          <AgentMultiPicker
            agents={agents}
            selected={productEditors}
            setSelected={setProductEditors}
            saving={saving}
          />

          <label className="inline-adder-vote-label">
            REVIEWERS (müssen approven, optional)
          </label>
          <AgentMultiPicker
            agents={agents}
            selected={productReviewers}
            setSelected={setProductReviewers}
            saving={saving}
          />
          {productReviewers.length > 0 && (
            <div className="inline-adder-vote-hint">
              Reviewer-Approval blockt den Status auf <strong>review</strong> bis
              alle approved haben (sections_approve-Workflow).
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function AgentMultiPicker({
  agents,
  selected,
  setSelected,
  saving,
}: {
  agents: string[]
  selected: string[]
  setSelected: (v: string[]) => void
  saving: boolean
}) {
  if (agents.length === 0) {
    return (
      <div className="inline-adder-vote-hint inline-adder-vote-empty-targets">
        Keine Doc-Agenten verfügbar.
      </div>
    )
  }
  return (
    <div className="inline-adder-vote-voters-list">
      {agents.map((name) => {
        const checked = selected.includes(name)
        return (
          <label key={name}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelected([...selected, name])
                } else {
                  setSelected(selected.filter((v) => v !== name))
                }
              }}
              disabled={saving}
            />
            {name}
          </label>
        )
      })}
    </div>
  )
}
